// @catalog F.010
/**
 * useSelectionHandlers — callbacks de selección de productos de ForecastPage
 * (toggle por fila/header, select/deselect de filtrados, preselección smart,
 * remoción desde el dialog). Extraído del orchestrator en el P7 split
 * 2026-06-10; sin cambio funcional vs versión inline previa.
 */
import { useCallback } from "react";

import { toggleHeaderSelection as toggleHeaderSelectionImpl } from "./page-handlers";
import { getProductUrgency } from "./selectors";
import type { ProductData } from "./types";

interface UseSelectionHandlersArgs {
  products: ProductData[];
  filteredAndSortedProducts: ProductData[];
  selectedIds: Set<string>;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setDialogBaseSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSelectedSupplierEstimatedAmount: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  setHideDeselected: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useSelectionHandlers({
  products,
  filteredAndSortedProducts,
  selectedIds,
  setSelectedIds,
  setDialogBaseSelectedIds,
  setSelectedSupplierEstimatedAmount,
  setHideDeselected,
}: UseSelectionHandlersArgs) {
  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const toggleRow = useCallback(
    (id: string) => {
      setSelectedSupplierEstimatedAmount(null);
      setSelectedIds((prev) => {
        const prod = products.find((p) => p.unifiedProductId === id);
        if (prod && prod.activeForPurchase === false) return prev;
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [products, setSelectedIds, setSelectedSupplierEstimatedAmount],
  );

  const toggleHeaderSelection = useCallback(() => {
    setSelectedIds((prev) =>
      toggleHeaderSelectionImpl({ filteredAndSortedProducts, prev }),
    );
  }, [filteredAndSortedProducts, setSelectedIds]);

  const selectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const p of filteredAndSortedProducts) {
        if (p.activeForPurchase !== false) next.add(p.unifiedProductId);
      }
      return next;
    });
  }, [filteredAndSortedProducts, setSelectedIds]);

  const deselectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const p of filteredAndSortedProducts) {
        next.delete(p.unifiedProductId);
      }
      return next;
    });
  }, [filteredAndSortedProducts, setSelectedIds]);

  const toggleHideDeselected = useCallback(() => {
    setHideDeselected((v) => !v);
  }, [setHideDeselected]);

  const runSmartPreselect = useCallback(() => {
    const ids = new Set(
      filteredAndSortedProducts
        .filter((p) => {
          const u = getProductUrgency(p);
          return u === "critical" || u === "warn";
        })
        .filter((p) => p.activeForPurchase !== false)
        .map((p) => p.unifiedProductId),
    );
    setSelectedIds(ids);
  }, [filteredAndSortedProducts, setSelectedIds]);

  // Item 2: deselecciona un producto desde el dialog de forecast. Quita el id
  // de selectedIds Y de dialogBaseSelectedIds para que handleCalcDialogClose
  // (que restaura la base al cerrar) no lo traiga de vuelta.
  const handleRemoveSelectedProduct = useCallback(
    (unifiedProductId: string) => {
      setSelectedIds((prev) => {
        if (!prev.has(unifiedProductId)) return prev;
        const next = new Set(prev);
        next.delete(unifiedProductId);
        return next;
      });
      setDialogBaseSelectedIds((prev) => {
        if (!prev.has(unifiedProductId)) return prev;
        const next = new Set(prev);
        next.delete(unifiedProductId);
        return next;
      });
    },
    [setDialogBaseSelectedIds, setSelectedIds],
  );

  return {
    isSelected,
    toggleRow,
    toggleHeaderSelection,
    selectAllFiltered,
    deselectAllFiltered,
    toggleHideDeselected,
    runSmartPreselect,
    handleRemoveSelectedProduct,
  };
}
